import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";
import { Eye, Plus, RefreshCw } from "lucide-react";
import {
  StatusPill, SlaIndicator, Modal, MessageBubble,
} from "@/components/phase4/Phase4Common";

const DISPUTE_TYPES = [
  "WEIGHT_DISCREPANCY", "DAMAGE_IN_TRANSIT", "WRONG_DELIVERY",
  "DELAYED_DELIVERY", "MISSING_ITEM", "BILLING_ERROR",
  "SERVICE_FAILURE", "OTHER",
];

export default function CustomerDisputes() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/v2/disputes", { params: { limit: 200 } });
      setItems(r.data?.items || []);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="customer-disputes">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">My Disputes</h1>
            <p className="text-slate-500 text-sm mt-0.5">Open issues and their resolution status</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="px-3 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded inline-flex items-center gap-1">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={() => setRaiseOpen(true)}
              data-testid="customer-raise-dispute-btn"
              className="px-3 py-1.5 bg-brand-500 hover:bg-brand-700 text-white text-xs font-semibold rounded inline-flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Raise Dispute
            </button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="customer-disputes-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Dispute ID", "Type", "Status", "Claimed ₹", "SLA", "Created", "Actions"].map((h) =>
                  <th key={h} className="text-left px-3 py-2 text-[10px] font-bold text-slate-500 uppercase">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? <tr><td colSpan={7} className="text-center py-8 text-slate-400">Loading…</td></tr>
                : items.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-slate-400">No disputes raised yet.</td></tr>
                : items.map((d) => (
                  <tr key={d.dispute_id} className="hover:bg-slate-50" data-testid={`customer-dispute-row-${d.dispute_id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{d.dispute_id}</td>
                    <td className="px-3 py-2 text-xs">{(d.dispute_type || "").replace(/_/g, " ")}</td>
                    <td className="px-3 py-2"><StatusPill status={d.status} /></td>
                    <td className="px-3 py-2 text-xs">₹{(d.claimed_amount || 0).toFixed(0)}</td>
                    <td className="px-3 py-2"><SlaIndicator deadline={d.sla_deadline} breached={d.sla_breach} resolved={d.resolved_at} /></td>
                    <td className="px-3 py-2 text-[11px] text-slate-500">
                      {d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <button onClick={() => setSelected(d)} className="text-brand-500 hover:text-brand-700"
                        data-testid={`customer-dispute-view-${d.dispute_id}`}>
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {raiseOpen && (
        <RaiseDisputeModal
          onClose={() => setRaiseOpen(false)}
          onCreated={(did) => { setRaiseOpen(false); load(); navigate(`/customer/disputes`); toast.success(`Dispute ${did} raised`); }}
        />
      )}
      {selected && (
        <Modal open onClose={() => setSelected(null)} title={`Dispute ${selected.dispute_id}`} testId="customer-dispute-detail" width="max-w-2xl">
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <StatusPill status={selected.status} />
              <SlaIndicator deadline={selected.sla_deadline} breached={selected.sla_breach} resolved={selected.resolved_at} />
            </div>
            <div className="grid grid-cols-2 gap-y-1 text-xs">
              <div className="text-slate-500">Type</div><div>{(selected.dispute_type || "").replace(/_/g, " ")}</div>
              <div className="text-slate-500">AWB</div><div className="font-mono">{selected.awb || "—"}</div>
              <div className="text-slate-500">Claimed</div><div className="font-semibold">₹{(selected.claimed_amount || 0).toFixed(2)}</div>
              <div className="text-slate-500">Resolution</div><div>{selected.resolution || "—"}</div>
            </div>
            <div className="bg-slate-50 rounded p-2 text-xs">{selected.description}</div>
            <div>
              <div className="text-[10px] font-bold uppercase text-slate-500 mb-2">Messages</div>
              <div className="max-h-[300px] overflow-y-auto space-y-1">
                {(selected.messages || []).length === 0 ? <div className="text-xs text-slate-400">No messages yet</div>
                  : selected.messages.map((m, i) => <MessageBubble key={i} msg={m} />)}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </DashboardLayout>
  );
}

function RaiseDisputeModal({ onClose, onCreated }) {
  const [shipQuery, setShipQuery] = useState("");
  const [shipment, setShipment] = useState(null);
  const [type, setType] = useState("");
  const [desc, setDesc] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const findShipment = async () => {
    if (!shipQuery.trim()) return;
    try {
      const r = await api.get(`/v2/tracking/${encodeURIComponent(shipQuery.trim())}`);
      if (r.data?.shipment_id) {
        setShipment({ id: r.data.shipment_id, awb: r.data.awb || shipQuery.trim() });
      }
    } catch { toast.error("Shipment not found"); }
  };

  const submit = async () => {
    if (!shipment || !type || !desc.trim()) { toast.error("Fill all required fields"); return; }
    setBusy(true);
    try {
      const r = await api.post("/v2/disputes", {
        shipment_id: shipment.id,
        dispute_type: type,
        description: desc,
        claimed_amount: parseFloat(amount || 0),
      });
      onCreated(r.data?.dispute_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  return (
    <Modal open onClose={onClose} title="Raise Dispute" testId="customer-raise-dispute-modal">
      <div className="space-y-3 text-sm">
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Shipment AWB *</label>
          <div className="flex gap-2">
            <input value={shipQuery} onChange={(e) => setShipQuery(e.target.value)}
              data-testid="customer-dispute-ship-search"
              className="flex-1 text-sm border border-slate-200 rounded px-2 py-1.5" />
            <button onClick={findShipment} className="px-3 py-1.5 bg-slate-100 text-xs font-semibold rounded">Find</button>
          </div>
          {shipment && <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded p-2 text-xs">✓ {shipment.awb}</div>}
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Dispute Type *</label>
          <select value={type} onChange={(e) => setType(e.target.value)}
            data-testid="customer-dispute-type"
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5">
            <option value="">— Select —</option>
            {DISPUTE_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Description *</label>
          <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={4}
            data-testid="customer-dispute-desc"
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Claimed Amount (₹)</label>
          <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
            data-testid="customer-dispute-amount"
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-4 py-1.5 border border-slate-200 text-xs rounded">Cancel</button>
        <button onClick={submit} disabled={busy}
          data-testid="customer-dispute-submit"
          className="px-4 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded disabled:opacity-50">
          Raise Dispute
        </button>
      </div>
    </Modal>
  );
}
